/**
 * Building Maintenance API client — Session 3.12.
 *
 * Types mirror the exact shapes returned by the API (raw DB shapes).
 * Presentation-layer values (badges, status labels, colours) are derived
 * client-side in the page component.
 */

import { apiFetch, apiDownload } from './api';
import type { RegisterPrintLog } from '../components/RegisterPrintHistory';
import { companyTodayIso } from './companyClock';

/* ------------------------------------------------------------------ */
/*  Shared enums                                                       */
/* ------------------------------------------------------------------ */

/** Whatever this tenant's category list says (2026-08-12). Not a closed set:
 *  the built-ins live on the server and each tenant adds to and removes from
 *  them, so this is the shape of the stored value, not its membership. */
export type AssetCategory = string;

export type AssetStatus = 'operational' | 'needs_attention' | 'out_of_service';

export type MaintenanceType = 'statutory' | 'ppm' | 'reactive' | 'inspection';
export type MaintenanceOutcome = 'pass' | 'advisory' | 'fail' | 'pending';

/* ------------------------------------------------------------------ */
/*  Asset Register                                                     */
/* ------------------------------------------------------------------ */

export interface AssetPublic {
  id: string;
  tenantId: string;
  siteId: string | null;
  category: AssetCategory;
  name: string;
  location: string;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  installDate: string | null;
  warrantyExpiry: string | null;
  status: AssetStatus;
  // v1_3 audit additions — surfaced from the schema for the demo's
  // two-badge card and detail panel.
  regulation: string | null;
  statutoryRequirement: string | null;
  lastInspected: string | null;
  contractorType: string | null;
  notes: string | null;
  nextInspectionDue: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AssetStats {
  total: number;
  operational: number;
  needsAttention: number;
  overdueCount: number;
}

export interface AssetListResult {
  assets: AssetPublic[];
  stats: AssetStats;
}

export interface CreateAssetPayload {
  name: string;
  category: string;
  location?: string;
  // BM-ADD-ASSET-001 (2026-05-28) — siteId + status added so the
  // AddAssetModal can pin a new asset to a site and pick an initial
  // status. Both already exist in the API's createAssetSchema; the
  // web type just hadn't surfaced them. siteId is nullable per the
  // API (omitted = server resolves to tenant's primary site).
  siteId?: string | null;
  status?: 'operational' | 'needs_attention' | 'out_of_service';
  // v1_3 audit — payload now mirrors the API's createAssetSchema. The
  // earlier `nextDueDate` field was speculative and never wired (next
  // due is computed server-side from maintenanceRecords).
  regulation?: string | null;
  statutoryRequirement?: string | null;
  lastInspected?: string | null;
  contractorType?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  installDate?: string | null;
  warrantyExpiry?: string | null;
  notes?: string | null;
}

export async function fetchAssets(
  opts: { category?: string; search?: string; siteId?: string } = {},
  signal?: AbortSignal,
): Promise<AssetListResult> {
  return apiFetch<AssetListResult>('/api/building-maintenance/assets', {
    method: 'GET',
    query: {
      category: opts.category || null,
      search: opts.search || null,
      // Multi-site Phase 2 — when set, scopes the asset list to one site.
      siteId: opts.siteId || null,
    },
    signal,
  });
}

export async function createAsset(payload: CreateAssetPayload): Promise<AssetPublic> {
  return apiFetch<AssetPublic>('/api/building-maintenance/assets', {
    method: 'POST',
    body: payload,
  });
}

/**
 * Edit a registered asset. The route is PATCH — this sent PUT, so the call
 * could never have succeeded. Nothing in the UI ever invoked it, which is why
 * a broken verb sat here unnoticed until an asset needed its install date
 * correcting after the fact (Shane, 2026-08-12).
 */
/** Remove a building asset from the register. Soft delete - records stay attached. */
export async function deleteAsset(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/building-maintenance/assets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function updateAsset(
  id: string,
  payload: Partial<CreateAssetPayload>,
): Promise<void> {
  await apiFetch<unknown>(`/api/building-maintenance/assets/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

/* ------------------------------------------------------------------ */
/*  Asset categories — the tenant's own list                           */
/* ------------------------------------------------------------------ */

/**
 * Shane, 2026-08-12: *"i dont have an asbestos register!"* The list is the
 * code's built-ins plus this tenant's additions and removals, resolved on the
 * server. The web asks for the answer rather than keeping its own copy — four
 * hand-written copies had already drifted once.
 */
export interface AssetCategoryOption {
  value: string;
  label: string;
  builtIn: boolean;
  /** Assets currently filed under it. Shown before removing one. */
  inUse: number;
}

export async function fetchAssetCategories(
  signal?: AbortSignal,
): Promise<AssetCategoryOption[]> {
  return apiFetch<AssetCategoryOption[]>('/api/building-maintenance/asset-categories', {
    method: 'GET',
    signal,
  });
}

/** Add a category, or rename one — including a built-in. Returns the new list. */
export async function saveAssetCategory(
  label: string,
  value?: string,
): Promise<AssetCategoryOption[]> {
  return apiFetch<AssetCategoryOption[]>('/api/building-maintenance/asset-categories', {
    method: 'POST',
    body: value ? { label, value } : { label },
  });
}

/** Take a category off this tenant's list. Assets already filed under it keep
 *  it — nothing is rewritten. Returns the new list. */
export async function removeAssetCategory(value: string): Promise<AssetCategoryOption[]> {
  return apiFetch<AssetCategoryOption[]>(
    `/api/building-maintenance/asset-categories/${encodeURIComponent(value)}`,
    { method: 'DELETE' },
  );
}

/* ------------------------------------------------------------------ */
/*  Maintenance Schedule                                               */
/* ------------------------------------------------------------------ */

export interface MaintenanceRecordPublic {
  id: string;
  tenantId: string;
  assetId: string;
  assetName: string;
  // v1_3 audit — joined asset fields. Schedule tab needs the regulation
  // badge text and the contractor type label, both of which live on the
  // asset (not the record).
  assetRegulation: string | null;
  assetContractorType: string | null;
  type: MaintenanceType;
  description: string;
  scheduledDate: string | null;
  completedDate: string | null;
  completedById: string | null;
  contractorId: string | null;
  outcome: MaintenanceOutcome;
  cost: string | null;
  certificateS3Key: string | null;
  nextDueDate: string | null;
  /** Advisory notes, defects found, actions raised. Empty when none were written. */
  notes: string;
  /**
   * Blockage K — recurring schedule this record fulfils. NULL = ad-hoc
   * one-off (no auto-recurrence). UI uses this to render the "Schedule:
   * {testType}" badge.
   */
  scheduleId: string | null;
  /**
   * Blockage K (K.6) — joined schedule.testType for the UI badge.
   * NULL when scheduleId is null.
   */
  scheduleTestType: string | null;
  /**
   * Blockage K — predecessor record when this row was auto-created by
   * the recurrence engine. UI uses this to render the "Auto-scheduled
   * from {date} test" indicator.
   */
  parentRecordId: string | null;
  /**
   * Blockage K (K.6) — predecessor's completed date (or scheduled date
   * fallback) so the UI can render "Auto-scheduled from {date} test"
   * without an N+1 lookup. NULL when parentRecordId is null.
   */
  parentCompletedDate: string | null;
  createdAt: string;
  /** The certificate FILE filed for this record, and the version to open. Null when none. */
  certificateDocumentId?: string | null;
  certificateVersionId?: string | null;
}

export interface ScheduleResult {
  records: MaintenanceRecordPublic[];
}

export interface CreateRecordPayload {
  assetId: string;
  type: MaintenanceType;
  description: string;
  scheduledDate?: string | null;
  completedDate?: string | null;
  completedById?: string | null;
  contractorId?: string | null;
  outcome?: MaintenanceOutcome;
  cost?: number | null;
  certificateS3Key?: string | null;
  nextDueDate?: string | null;
  notes?: string;
  /** Blockage K — link new record to a recurring schedule. */
  scheduleId?: string | null;
}

export async function fetchSchedule(
  opts: { type?: MaintenanceType | ''; overdueOnly?: boolean } = {},
  signal?: AbortSignal,
): Promise<ScheduleResult> {
  return apiFetch<ScheduleResult>('/api/building-maintenance/schedule', {
    method: 'GET',
    query: {
      type: opts.type || null,
      overdueOnly: opts.overdueOnly ? 'true' : null,
    },
    signal,
  });
}

export async function createRecord(
  payload: CreateRecordPayload,
): Promise<MaintenanceRecordPublic> {
  return apiFetch<MaintenanceRecordPublic>('/api/building-maintenance/records', {
    method: 'POST',
    body: payload,
  });
}

/**
 * The details an edit may correct (2026-09-29). Not the asset, the schedule,
 * the certificate or who completed it: the route refuses those, because a
 * statutory record corrected onto another asset is a different record.
 */
export type EditRecordPayload = Partial<
  Pick<
    CreateRecordPayload,
    | 'type'
    | 'description'
    | 'scheduledDate'
    | 'completedDate'
    | 'contractorId'
    | 'outcome'
    | 'cost'
    | 'nextDueDate'
    | 'notes'
  >
>;

/** Correct a maintenance record's details. Director, manager or admin. */
export async function updateRecord(
  id: string,
  payload: EditRecordPayload,
): Promise<MaintenanceRecordPublic> {
  return apiFetch<MaintenanceRecordPublic>(`/api/building-maintenance/records/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

/* ------------------------------------------------------------------ */
/*  Recurring schedules (Blockage K)                                   */
/* ------------------------------------------------------------------ */

/**
 * One row per (asset × test type) — defines a recurring cadence for that
 * test against that asset. A fire alarm has up to 4 schedules (weekly
 * bell test, monthly battery, 6-monthly engineer, annual full cert).
 */
export interface MaintenanceSchedulePublic {
  id: string;
  tenantId: string;
  assetId: string;
  testType: string;
  description: string;
  type: MaintenanceType;
  /** Monthly+ cadence (12 = annual). Mutually exclusive with frequencyDays. */
  frequencyMonths: number | null;
  /** Sub-monthly cadence (7 = weekly bell test). Mutually exclusive with frequencyMonths. */
  frequencyDays: number | null;
  regulation: string | null;
  contractorType: string | null;
  active: boolean;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSchedulePayload {
  testType: string;
  description: string;
  type: MaintenanceType;
  frequencyMonths?: number | null;
  frequencyDays?: number | null;
  regulation?: string | null;
  contractorType?: string | null;
  active?: boolean;
  notes?: string | null;
}

export type UpdateSchedulePayload = Partial<CreateSchedulePayload>;

export interface ListSchedulesResult {
  schedules: MaintenanceSchedulePublic[];
}

/** GET /api/building-maintenance/assets/:assetId/schedules */
export async function listSchedulesForAsset(
  assetId: string,
  signal?: AbortSignal,
): Promise<ListSchedulesResult> {
  return apiFetch<ListSchedulesResult>(
    `/api/building-maintenance/assets/${assetId}/schedules`,
    { method: 'GET', signal },
  );
}

/** POST /api/building-maintenance/assets/:assetId/schedules */
export async function createSchedule(
  assetId: string,
  payload: CreateSchedulePayload,
): Promise<MaintenanceSchedulePublic> {
  return apiFetch<MaintenanceSchedulePublic>(
    `/api/building-maintenance/assets/${assetId}/schedules`,
    { method: 'POST', body: payload },
  );
}

/** PATCH /api/building-maintenance/schedules/:id */
export async function updateSchedule(
  id: string,
  payload: UpdateSchedulePayload,
): Promise<MaintenanceSchedulePublic> {
  return apiFetch<MaintenanceSchedulePublic>(
    `/api/building-maintenance/schedules/${id}`,
    { method: 'PATCH', body: payload },
  );
}

/** DELETE /api/building-maintenance/schedules/:id (soft archive) */
export async function archiveSchedule(
  id: string,
): Promise<{ id: string; archivedAt: string }> {
  return apiFetch<{ id: string; archivedAt: string }>(
    `/api/building-maintenance/schedules/${id}`,
    { method: 'DELETE' },
  );
}

/* ------------------------------------------------------------------ */
/*  CSV import (assets — commit 1 of 3)                                */
/* ------------------------------------------------------------------ */

export interface BmImportRowError {
  rowNumber: number;
  reason: string;
  columnName?: string;
}

export interface BmImportResult {
  dryRun: boolean;
  importType: 'assets' | 'schedules' | 'records';
  cleanRows: number;
  errorRows: BmImportRowError[];
  importedRows: number;
}

export type BmImportTemplateType = 'assets' | 'schedules' | 'records';

/**
 * Trigger a CSV-template download for the named importer. The browser
 * receives a text/csv body with the canonical headers + one example
 * row that the user deletes before adding their data.
 *
 * MUST go through apiDownload (2026-08-16). This used to build an anchor
 * and click it, on the reasoning that apiFetch expects a JSON envelope and
 * a CSV is not one — true, but it threw the Authorization header away with
 * the envelope. `/api/building-maintenance/*` sits behind authenticateToken,
 * which reads the Bearer token from that header and has no cookie fallback,
 * so the request arrived unauthenticated and answered 401.
 *
 * The anchor carried `download`, so the browser saved the 401 body under the
 * template's own filename: `bm-assets-template.csv` containing
 * {"success":false,...}. It looked like it had worked, which is why it
 * survived a static audit that ticked this off as calling a real endpoint.
 *
 * apiDownload does the same job with the token attached, and throws ApiError
 * on a non-2xx instead of saving the error as the file. Five other sections
 * had the identical fault — see IMPORT-EXPORT-AUDIT-001 FAULT 5.
 */
export async function downloadBmImportTemplate(
  type: BmImportTemplateType,
): Promise<void> {
  await apiDownload(
    '/api/building-maintenance/import/template',
    `bm-${type}-template.csv`,
    { query: { type } },
  );
}

/**
 * POST /api/building-maintenance/import/assets
 * dryRun=true returns the validation summary without committing;
 * dryRun=false commits clean rows in a single transaction (errors don't
 * block clean rows — same behaviour as the HR importer).
 */
export async function importBuildingAssets(input: {
  headers: string[];
  rows: Record<string, string>[];
  dryRun: boolean;
}): Promise<BmImportResult> {
  return apiFetch<BmImportResult>('/api/building-maintenance/import/assets', {
    method: 'POST',
    body: input,
  });
}

/**
 * POST /api/building-maintenance/import/schedules
 *
 * Composite asset lookup by Asset Name + Site Code (smart default for
 * single-site tenants). dryRun + confirm semantics match the assets
 * importer.
 */
export async function importMaintenanceSchedules(input: {
  headers: string[];
  rows: Record<string, string>[];
  dryRun: boolean;
}): Promise<BmImportResult> {
  return apiFetch<BmImportResult>('/api/building-maintenance/import/schedules', {
    method: 'POST',
    body: input,
  });
}

/**
 * POST /api/building-maintenance/import/records
 *
 * Bulk-import historical maintenance records. Always imports as ad-hoc
 * (no schedule link); users link individual records to recurring
 * schedules via the UI when they want the cadence engine to track them.
 */
export async function importMaintenanceRecords(input: {
  headers: string[];
  rows: Record<string, string>[];
  dryRun: boolean;
}): Promise<BmImportResult> {
  return apiFetch<BmImportResult>('/api/building-maintenance/import/records', {
    method: 'POST',
    body: input,
  });
}

/* ------------------------------------------------------------------ */
/*  Legionella Records                                                 */
/* ------------------------------------------------------------------ */

export interface LegionellaSchemePublic {
  id: string;
  tenantId: string;
  siteId: string | null;
  riskAssessmentDate: string | null;
  riskAssessmentS3Key: string | null;
  responsiblePersonId: string | null;
  // v1_3 audit — surfaced from schema. L8 banner needs this for the
  // "Next RA due" pill (was hardcoded to "—").
  nextDueDate: string | null;
  /** Who carried out the assessment. Null on assessments filed before 2026-09-30. */
  assessorName: string | null;
  /** Read from the tenant's own records on GET (TODO 2017) - never invented. */
  siteName?: string | null;
  responsiblePersonName?: string | null;
  /** The filed file, when one is linked - the row's Certificate opens it. */
  riskAssessmentDocumentId?: string | null;
  riskAssessmentVersionId?: string | null;
  // v1_3 audit follow-up — 'current' | 'superseded'. Demo's RA table
  // shows the prior assessment as a muted superseded row.
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface LegionellaCheckPublic {
  id: string;
  /** BM-LEGIONELLA-READ-FIX-001 — null for a reading taken before any risk assessment was uploaded. */
  schemeId: string | null;
  tenantId: string;
  date: string;
  systemChecked: string;
  temperatureReading: string;
  inRange: boolean;
  recordedById: string | null;
  notes: string | null;
  createdAt: string;
}

export interface LegionellaResult {
  /** Most recent (current) scheme — convenience accessor for single-row UIs. */
  scheme: LegionellaSchemePublic | null;
  /**
   * Full ordered list of schemes (current first, then superseded).
   * Drives the demo's two-row Risk Assessment table. v1_3 audit follow-up.
   */
  schemes: LegionellaSchemePublic[];
  checks: LegionellaCheckPublic[];
}

export async function fetchLegionella(signal?: AbortSignal): Promise<LegionellaResult> {
  return apiFetch<LegionellaResult>('/api/hs/legionella', {
    method: 'GET',
    signal,
  });
}

export interface AddLegionellaCheckPayload {
  date: string;
  systemChecked: string;
  temperatureReading: number;
  recordedById?: string | null;
  notes?: string | null;
}

export async function addLegionellaCheck(
  payload: AddLegionellaCheckPayload,
): Promise<LegionellaCheckPublic> {
  return apiFetch<LegionellaCheckPublic>('/api/hs/legionella/checks', {
    method: 'POST',
    body: payload,
  });
}

export interface UploadRiskAssessmentPayload {
  riskAssessmentDate: string;
  /** The uploaded file's name; the file itself is filed in the Document Register. */
  riskAssessmentS3Key: string;
  /** Omitted: the server uses the tenant's primary site. */
  siteId?: string | null;
  responsiblePersonId: string;
  nextDueDate?: string | null;
  assessorName?: string | null;
}

export async function uploadRiskAssessment(
  payload: UploadRiskAssessmentPayload,
): Promise<LegionellaSchemePublic> {
  return apiFetch<LegionellaSchemePublic>('/api/hs/legionella/ra', {
    method: 'POST',
    body: payload,
  });
}

/* ------------------------------------------------------------------ */
/*  First Aid (Section 12 v1_3 — Health and Safety (First Aid) Regs    */
/*  1981 / BS 8599-1)                                                  */
/* ------------------------------------------------------------------ */

export type FirstAidKitType =
  | 'small_workplace'
  | 'medium_workplace'
  | 'large_workplace'
  | 'travel'
  | 'other'
  | 'spill_kit';

export type FirstAidCheckOutcome = 'fully_stocked' | 'items_missing' | 'not_checked';

export type FirstAidRestockStatus = 'not_required' | 'ordered' | 'urgent' | 'restocked';

export interface FirstAidBoxPublic {
  id: string;
  siteId: string;
  name: string;
  location: string;
  kitType: FirstAidKitType;
  checkFrequencyMonths: number;
  responsiblePersonId: string | null;
  /*
   * BM-FIRSTAID-ASSET-001 (2026-09-14) — the asset this box sits at.
   *
   * Null is the ORDINARY case, not a gap: plenty of boxes are screwed to a
   * wall next to nothing in particular, and a tenant with no asset register
   * at all must still be able to add one. That was the fault fixed on
   * 12 Sep (BM-FIRSTAID-SITE-001) and it is not being reintroduced here.
   *
   * name/category are denormalised on read so the card needs no second
   * query; both are null when buildingAssetId is.
   */
  buildingAssetId: string | null;
  buildingAssetName: string | null;
  buildingAssetCategory: string | null;
  dateInstalled: string | null;
  notes: string;
  createdAt: string;
  lastCheckDate: string | null;
  lastCheckOutcome: FirstAidCheckOutcome | null;
  /** The latest check's restock answer; 'restocked' means the missing items are back. */
  lastRestockStatus: FirstAidRestockStatus | null;
  nextCheckDue: string | null;
  isOverdue: boolean;
}

export interface FirstAidCheckPublic {
  id: string;
  boxId: string;
  date: string;
  checkedById: string | null;
  /** Who recorded the check; null when the record does not say. */
  checkedByName: string | null;
  outcome: FirstAidCheckOutcome;
  itemsChecked: Record<string, boolean> | null;
  missingItems: string;
  restockStatus: FirstAidRestockStatus;
  nextCheckDue: string | null;
  notes: string;
  createdAt: string;
}

export interface FirstAidStats {
  totalBoxes: number;
  fullyStocked: number;
  itemsMissing: number;
  checksOverdue: number;
}

export interface FirstAidListResult {
  boxes: FirstAidBoxPublic[];
  stats: FirstAidStats;
}

export interface CreateFirstAidBoxPayload {
  siteId: string;
  name: string;
  location: string;
  kitType: FirstAidKitType;
  checkFrequencyMonths?: number;
  responsiblePersonId?: string;
  /** Optional, and it stays optional — see FirstAidBoxPublic above. */
  buildingAssetId?: string | null;
  dateInstalled?: string;
  notes?: string;
}

export interface LogFirstAidCheckPayload {
  boxId: string;
  date: string;
  checkedById?: string;
  outcome: FirstAidCheckOutcome;
  itemsChecked?: Record<string, boolean>;
  missingItems?: string;
  restockStatus?: FirstAidRestockStatus;
  notes?: string;
}

export async function fetchFirstAidBoxes(
  opts: { siteId?: string } = {},
  signal?: AbortSignal,
): Promise<FirstAidListResult> {
  return apiFetch<FirstAidListResult>('/api/hs/first-aid', {
    method: 'GET',
    query: { siteId: opts.siteId || null },
    signal,
  });
}

export async function createFirstAidBox(
  payload: CreateFirstAidBoxPayload,
): Promise<FirstAidBoxPublic> {
  return apiFetch<FirstAidBoxPublic>('/api/hs/first-aid', {
    method: 'POST',
    body: payload,
  });
}

export async function logFirstAidCheck(
  payload: LogFirstAidCheckPayload,
): Promise<FirstAidCheckPublic> {
  return apiFetch<FirstAidCheckPublic>('/api/hs/first-aid/checks', {
    method: 'POST',
    body: payload,
  });
}

/** A check in the full history, with the box it was made on. */
export interface FirstAidCheckListItem extends FirstAidCheckPublic {
  boxName: string;
  boxLocation: string;
  boxKitType: FirstAidKitType;
  siteId: string;
}

export async function fetchFirstAidChecks(
  siteId: string | null,
  signal?: AbortSignal,
): Promise<FirstAidCheckListItem[]> {
  const qs = siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
  const data = await apiFetch<{ checks: FirstAidCheckListItem[] }>(`/api/hs/first-aid/checks${qs}`, {
    method: 'GET',
    signal,
  });
  return data.checks;
}

export async function fetchFirstAidHistory(
  boxId: string,
  signal?: AbortSignal,
): Promise<FirstAidCheckPublic[]> {
  const data = await apiFetch<{ history: FirstAidCheckPublic[] }>(
    `/api/hs/first-aid/${boxId}/history`,
    { method: 'GET', signal },
  );
  return data.history;
}

/* ------------------------------------------------------------------ */
/*  Asbestos register (Section 12 v1_3 — demo v1_26)                  */
/*  Lives inside the Legionella tab in the UI but is its own dataset. */
/* ------------------------------------------------------------------ */

export type AsbestosRecordType =
  | 'management_survey'
  | 'refurbishment_survey'
  | 're_inspection'
  | 'removal';

export type AsbestosRiskLevel = 'low' | 'medium' | 'high' | 'not_applicable';

export type AsbestosCondition =
  | 'good'
  | 'good_encapsulated'
  | 'damaged_minor'
  | 'damaged_significant'
  | 'removed';

export interface AsbestosRecordPublic {
  id: string;
  siteId: string;
  documentTitle: string;
  recordType: AsbestosRecordType;
  surveyDate: string;
  surveyorName: string;
  contractorId: string | null;
  riskLevel: AsbestosRiskLevel;
  riskNote: string;
  nextInspectionDue: string | null;
  documentS3Key: string | null;
  status: string;
  notes: string;
  createdAt: string;
}

export interface AsbestosAcmPublic {
  id: string;
  siteId: string;
  location: string;
  materialType: string;
  condition: AsbestosCondition;
  riskLevel: AsbestosRiskLevel;
  managementAction: string;
  responsiblePersonId: string | null;
  lastInspected: string | null;
  notes: string;
  createdAt: string;
}

export interface AsbestosRegisterResult {
  records: AsbestosRecordPublic[];
  acms: AsbestosAcmPublic[];
}

export interface CreateAsbestosRecordPayload {
  siteId: string;
  documentTitle: string;
  recordType: AsbestosRecordType;
  surveyDate: string;
  surveyorName: string;
  contractorId?: string;
  riskLevel: AsbestosRiskLevel;
  riskNote?: string;
  nextInspectionDue?: string;
  documentS3Key?: string;
  notes?: string;
}

export interface CreateAsbestosAcmPayload {
  siteId: string;
  location: string;
  materialType: string;
  condition: AsbestosCondition;
  riskLevel: AsbestosRiskLevel;
  managementAction?: string;
  responsiblePersonId?: string;
  lastInspected?: string;
  notes?: string;
}

export async function fetchAsbestosRegister(
  signal?: AbortSignal,
): Promise<AsbestosRegisterResult> {
  return apiFetch<AsbestosRegisterResult>('/api/hs/asbestos', {
    method: 'GET',
    signal,
  });
}

export async function createAsbestosRecord(
  payload: CreateAsbestosRecordPayload,
): Promise<AsbestosRecordPublic> {
  return apiFetch<AsbestosRecordPublic>('/api/hs/asbestos/records', {
    method: 'POST',
    body: payload,
  });
}

export async function createAsbestosAcm(
  payload: CreateAsbestosAcmPayload,
): Promise<AsbestosAcmPublic> {
  return apiFetch<AsbestosAcmPublic>('/api/hs/asbestos/acms', {
    method: 'POST',
    body: payload,
  });
}

// HS-ASB-001 Phase 3 (2026-05-24) — per-ACM reinspection history.
// Each AsbestosAcm carries a chain of reinspection events captured
// as one row per inspection. CAR 2012 Reg 4(8) audit need.

export interface AsbestosAcmReinspectionPublic {
  id: string;
  acmId: string;
  inspectedDate: string;
  inspectedByName: string;
  conditionBefore: AsbestosCondition;
  conditionAfter: AsbestosCondition;
  managementActionUpdated: string | null;
  notes: string;
  documentS3Key: string | null;
  createdAt: string;
}

export interface CreateAcmReinspectionPayload {
  inspectedDate: string;
  inspectedByName: string;
  conditionAfter: AsbestosCondition;
  managementActionUpdated?: string;
  notes?: string;
  documentS3Key?: string;
}

export async function fetchAcmReinspections(
  acmId: string,
  signal?: AbortSignal,
): Promise<AsbestosAcmReinspectionPublic[]> {
  const data = await apiFetch<{ reinspections: AsbestosAcmReinspectionPublic[] }>(
    `/api/hs/asbestos/acms/${acmId}/reinspections`,
    { method: 'GET', signal },
  );
  return data.reinspections;
}

export async function createAcmReinspection(
  acmId: string,
  payload: CreateAcmReinspectionPayload,
): Promise<AsbestosAcmReinspectionPublic> {
  return apiFetch<AsbestosAcmReinspectionPublic>(
    `/api/hs/asbestos/acms/${acmId}/reinspections`,
    { method: 'POST', body: payload },
  );
}

/* ------------------------------------------------------------------ */
/*  Badge / overdue count                                              */
/* ------------------------------------------------------------------ */

export async function fetchOverdueCount(signal?: AbortSignal): Promise<number> {
  const data = await apiFetch<{ count: number }>('/api/building-maintenance/overdue-count', {
    method: 'GET',
    signal,
  });
  return data.count;
}

/** @deprecated Use fetchOverdueCount instead */
export const fetchBuildingMaintenanceBadge = fetchOverdueCount;

/**
 * The asset register as a controlled print (2026-08-30).
 *
 * Shane: *"no print area in any of these"*. Until now the only way to take this
 * register off the platform was CSV - a spreadsheet, which is exactly the
 * artefact document control exists to get away from, since anyone can open it
 * and change a date.
 *
 * The filters are passed through so a site manager can print their own site and
 * the counts on the document match the rows on it.
 */
export async function downloadBuildingRegisterPdf(
  opts: { siteId?: string; category?: string; search?: string } = {},
): Promise<void> {
  const stamp = companyTodayIso();
  await apiDownload('/api/building-maintenance/register.pdf', `building-asset-register-${stamp}.pdf`, {
    query: {
      siteId: opts.siteId ?? null,
      category: opts.category ?? null,
      search: opts.search ?? null,
    },
  });
}

/** Who has printed the asset register, and when. */
export async function fetchBuildingPrintLog(signal?: AbortSignal): Promise<RegisterPrintLog> {
  return apiFetch<RegisterPrintLog>('/api/building-maintenance/print-log', {
    method: 'GET',
    signal,
  });
}

/**
 * The Maintenance schedule TAB as a controlled print (2026-08-30).
 *
 * A different document from the asset register and from the cross-register
 * schedule under Assets & Maintenance: this one answers what was carried out,
 * by whom, with what outcome and whether a certificate is on file.
 */
export async function downloadBuildingSchedulePdf(): Promise<void> {
  const stamp = companyTodayIso();
  await apiDownload(
    '/api/building-maintenance/schedule.pdf',
    `building-maintenance-schedule-${stamp}.pdf`,
  );
}

export async function fetchBuildingSchedulePrintLog(
  signal?: AbortSignal,
): Promise<RegisterPrintLog> {
  return apiFetch<RegisterPrintLog>('/api/building-maintenance/schedule-print-log', {
    method: 'GET',
    signal,
  });
}
